import { useState, useEffect, useRef, useCallback } from 'react';
import { webOrigin } from '../utils/brand';
import { API_BASE } from '../utils/apiBase';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { ticketService } from '../services';
import { useAuth } from '../context/AuthContext';
import SignalPassArt from '../components/SignalPassArt';
import PortalLogo from '../components/PortalLogo';

/**
 * TicketPage — one ticket, or the whole batch when you bought several.
 *
 * A group buy is a horizontal deck: the current ticket fills the screen, the next
 * one peeks in from the right, and dots below say how many there are. You hand the
 * phone over and swipe. Scanning them one at a time is the actual job at a door,
 * so the QR is what each card is built around.
 *
 * The page carries no Navbar on purpose — it's a thing you hold up to a stranger,
 * not a screen you browse from — but it still needs a way out, so there's a single
 * control top-left. Where it goes depends on how you got here: BACK when you came
 * from inside the app, HOME when a shared link or a QR scan opened this cold and
 * "back" would leave the site entirely.
 */
export default function TicketPage() {
  const { ticketCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [ticket, setTicket] = useState(null);
  // Every ticket on the same purchase, when the viewer owns them.
  const [siblings, setSiblings] = useState([]);
  const [index, setIndex] = useState(0);
  const deckRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applePass, setApplePass] = useState(false);
  const [googlePass, setGooglePass] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const response = await ticketService.getTicket(ticketCode);
        setTicket(response.data);
      } catch (err) {
        setError(err.response?.data?.error?.message || 'Ticket not found');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [ticketCode]);

  // Pull the rest of the batch. Auth-gated and ownership-checked server-side, so a
  // shared ticket link never exposes anyone else's — it just stays a single ticket.
  useEffect(() => {
    if (!isAuthenticated || !ticket?.applicationId) return;
    let cancelled = false;
    (async () => {
      const r = await ticketService.getTicketsForApplication(ticket.applicationId);
      if (cancelled) return;
      const all = r?.data || [];
      if (all.length > 1) {
        setSiblings(all);
        const i = all.findIndex((t) => String(t.ticketCode) === String(ticketCode));
        setIndex(i >= 0 ? i : 0);
      }
    })().catch(() => { /* a single ticket is a fine fallback */ });
    return () => { cancelled = true; };
  }, [isAuthenticated, ticket?.applicationId, ticketCode]);

  // Track which card is centred as you swipe, so the dots stay honest.
  const onDeckScroll = useCallback(() => {
    const el = deckRef.current;
    if (!el || siblings.length < 2) return;
    const card = el.scrollWidth / siblings.length;
    setIndex(Math.round(el.scrollLeft / card));
  }, [siblings.length]);

  const goTo = (i) => {
    const el = deckRef.current;
    if (!el) return;
    el.scrollTo({ left: (el.scrollWidth / siblings.length) * i, behavior: 'smooth' });
  };

  // Probe whether Apple Wallet passes are configured (hidden until certs are on prod).
  useEffect(() => {
    const base = API_BASE;
    fetch(`${base}/tickets/wallet/capabilities`)
      .then((r) => r.json())
      .then((d) => { setApplePass(!!d?.data?.applePass); setGooglePass(!!d?.data?.googlePass); })
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center font-mono">
        <div className="animate-pulse text-xl tracking-wider">LOADING TICKET...</div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center font-mono p-6">
        <h1 className="text-2xl font-bold tracking-wider mb-4">TICKET NOT FOUND</h1>
        <p className="text-gray-400 mb-6">{error || 'This ticket does not exist.'}</p>
        <Link to="/" className="border border-white px-6 py-3 tracking-wider hover:bg-white hover:text-black transition-colors">
          GO HOME
        </Link>
      </div>
    );
  }

  // (The QR, date and label lookups live in TicketCard — every card needs its own.)

  const deck = siblings.length > 1 ? siblings : [ticket];
  const isDeck = deck.length > 1;
  // A signal isn't a real ticket — it's "we're all meeting up." When the pass is
  // for a signal, drop the ticket layout for the playful pass. Signals are always a
  // single seat, so this never collides with the multi-ticket deck.
  const isSignal = !isDeck && !!ticket.isSignal;

  // react-router stamps the first entry of a session 'default'. Anything else means
  // there's app history behind us and going back stays on Portal.
  const cameFromApp = location.key !== 'default';
  const leave = () => (cameFromApp ? navigate(-1) : navigate('/'));

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <div className={`mx-auto ${isDeck ? 'max-w-lg' : 'max-w-md'} p-6 pt-6`}>
        {/* The way out. */}
        <div className="mb-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={leave}
            className="border border-white/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-white hover:text-black"
          >
            {cameFromApp ? '\u2039 Back' : '\u2039 Home'}
          </button>
          {isAuthenticated && (
            <Link
              to="/my-tickets"
              className="border border-white/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-white hover:text-black"
            >
              All tickets
            </Link>
          )}
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          {/* The logo is the lockup, never type, and never a grey — the word is #000 or
              #FFF only (docs/plans/2026-09-portal-logo). On black it takes white. */}
          <div className="mb-2 flex justify-center text-white"><PortalLogo cap={24} /></div>
          <div className="text-3xl font-bold tracking-wider" style={isSignal ? { color: '#03FFFF' } : undefined}>
            {isSignal ? 'YOUR PASS' : isDeck ? `YOUR TICKETS · ${deck.length}` : 'YOUR TICKET'}
          </div>
          {isDeck && (
            <p className="mt-2 text-[11px] tracking-wider text-gray-500">
              Swipe for the next one · scan them one at a time
            </p>
          )}
        </div>

        {isSignal ? (
          <SignalPassCard ticket={ticket} />
        ) : isDeck ? (
          <>
            {/* The deck: each card nearly full width so the next one peeks in. */}
            <div
              ref={deckRef}
              onScroll={onDeckScroll}
              className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2"
              style={{ scrollbarWidth: 'none' }}
            >
              {deck.map((t, i) => (
                <div key={t.ticketCode} className="w-[86%] flex-shrink-0 snap-center">
                  <TicketCard ticket={t} position={i + 1} of={deck.length} applePass={applePass} googlePass={googlePass} />
                </div>
              ))}
            </div>

            {/* Dots — how many, and which one you're on. */}
            <div className="mt-4 flex items-center justify-center gap-2">
              {deck.map((t, i) => (
                <button
                  key={t.ticketCode}
                  type="button"
                  aria-label={`Ticket ${i + 1} of ${deck.length}`}
                  onClick={() => goTo(i)}
                  className={`h-2 w-2 transition-colors ${i === index ? 'bg-white' : 'bg-white/30'}`}
                />
              ))}
            </div>
          </>
        ) : (
          <TicketCard ticket={ticket} applePass={applePass} googlePass={googlePass} />
        )}

        {/* CTA — only for logged-out guests (a shared ticket link). Signed-in users
            already have an account, so pushing "create account" is wrong. */}
        {!isAuthenticated && (
          <div className="mt-8 text-center space-y-3">
            <p className="text-gray-500 text-sm">
              Sign up for Portal to manage your tickets and discover more events.
            </p>
            <Link
              to="/register"
              className="inline-block border border-white px-8 py-3 tracking-wider hover:bg-white hover:text-black transition-colors text-sm"
            >
              CREATE ACCOUNT
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/** A link to the event page when the ticket knows its code; the same block, unlinked, when it doesn't. */
function EventLink({ code, className, label, children }) {
  if (!code) return <div className={className}>{children}</div>;
  return <Link to={`/events/${code}`} className={className} aria-label={label} data-testid="ticket-event-link">{children}</Link>;
}

/**
 * One ticket card — poster, event, guest, status, and the QR that gets scanned.
 * Identical whether it's the only ticket or one of several in a deck.
 */
function TicketCard({ ticket, position, of, applePass, googlePass }) {
  const ticketUrl = `${webOrigin()}/ticket/${ticket.ticketCode}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(ticketUrl)}&size=250x250&bgcolor=000000&color=ffffff`;

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  const statusLabel = { paid: 'CONFIRMED', accepted: 'ACCEPTED', pending: 'PENDING' };
  const attendanceLabel = { attended: 'CHECKED IN', no_show: 'NO SHOW', pending: 'NOT YET CHECKED IN' };
  // TicketDetailDto's names: the seat's holder if it was sent on, else whoever bought it.
  const guestName = ticket.recipientName || ticket.buyerName || 'Guest';
  const status = ticket.applicationStatus;

  return (
    <div className="border border-white">
      {position && (
        <div className="flex items-center justify-between border-b border-white/20 px-4 py-2 text-[10px] tracking-[0.2em] text-gray-400">
          <span>TICKET {position} OF {of}</span>
          {ticket.recipientName && <span className="truncate">{ticket.recipientName}</span>}
        </div>
      )}

      {/* The poster and the event block are the way back to the event page (for the info a
          ticket doesn't carry) — before this the only route was back out through the dashboard. */}
      {ticket.eventPosterUrl && (
        <EventLink code={ticket.eventInviteCode} className="block h-40 w-full overflow-hidden" label={`Open ${ticket.eventTitle}`}>
          <img src={ticket.eventPosterUrl} alt={ticket.eventTitle} className="h-full w-full object-cover" />
        </EventLink>
      )}

      <EventLink code={ticket.eventInviteCode} className="block border-b border-white/20 p-5 hover:bg-white/5 transition-colors" label={`Open ${ticket.eventTitle}`}>
        <h2 className="mb-2 text-lg font-bold tracking-wider">{ticket.eventTitle}</h2>
        <p className="text-sm text-gray-400">{formatDate(ticket.eventDateTime)}</p>
        {ticket.eventLocationCity && <p className="mt-1 text-sm text-gray-400">{ticket.eventLocationCity}</p>}
        {ticket.eventInviteCode && <p className="mt-2 text-[10px] tracking-[0.2em] text-gray-500">EVENT PAGE →</p>}
      </EventLink>

      {ticket.eventLocationAddress && (
        <div className="border-b border-white/20 bg-white/5 p-5">
          <p className="mb-1 text-xs tracking-wider text-gray-500">LOCATION</p>
          <p className="text-sm">{ticket.eventLocationAddress}</p>
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(ticket.eventLocationAddress)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-block border border-white px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white hover:text-black"
          >
            GET DIRECTIONS
          </a>
        </div>
      )}

      <div className="flex items-center justify-between border-b border-white/20 p-5">
        <div className="min-w-0">
          <p className="mb-1 text-xs tracking-wider text-gray-500">GUEST</p>
          <p className="truncate text-base font-bold">{guestName}</p>
        </div>
        <div className="ml-4 text-right">
          <p className="mb-1 text-xs tracking-wider text-gray-500">STATUS</p>
          <p className={`text-sm font-bold ${status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
            {statusLabel[status] || status?.toUpperCase() || '—'}
          </p>
        </div>
      </div>

      {ticket.attendanceStatus && (
        <div className="border-b border-white/20 px-5 py-3">
          <p className={`text-xs font-bold tracking-wider ${ticket.attendanceStatus === 'attended' ? 'text-green-400' : 'text-gray-400'}`}>
            {attendanceLabel[ticket.attendanceStatus] || ticket.attendanceStatus?.toUpperCase()}
          </p>
        </div>
      )}

      <div className="flex flex-col items-center p-5">
        <p className="mb-3 text-xs tracking-wider text-gray-500">SCAN FOR ENTRY</p>
        <img src={qrUrl} alt="Ticket QR code" className="h-[200px] w-[200px]" />
        {(applePass || googlePass) && (
          <div className="mt-5 flex flex-col items-center gap-3 sm:flex-row">
            {applePass && (
              <a
                href={`${API_BASE}/tickets/${ticket.ticketCode}/apple-pass`}
                className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-2.5 font-mono text-xs font-bold tracking-wider text-black hover:opacity-90"
              >
                 Add to Apple Wallet
              </a>
            )}
            {googlePass && (
              <a
                href={`${API_BASE}/tickets/${ticket.ticketCode}/google-pass`}
                className="inline-flex items-center gap-2 rounded-lg border border-white bg-black px-5 py-2.5 font-mono text-xs font-bold tracking-wider text-white hover:opacity-90"
              >
                Add to Google Wallet
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SignalPassCard — the playful pass a signal gets instead of a ticket.
 *
 * It's not really a ticket, it's "you're in, come hang." So: a welcome, the host's
 * one-line signal in their own words, the stick-figure art, and — because the whole
 * point is walking through a door — the same scannable QR a real ticket carries, built
 * from the same /ticket/{code} URL the door scanner decodes. Cyan is the signal accent.
 */
function SignalPassCard({ ticket }) {
  const CYAN = '#03FFFF';
  const ticketUrl = `${webOrigin()}/ticket/${ticket.ticketCode}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(ticketUrl)}&size=250x250&bgcolor=000000&color=ffffff`;

  const when = ticket.eventDateTime
    ? new Date(ticket.eventDateTime).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : null;
  const checkedIn = ticket.attendanceStatus === 'attended';
  const guest = ticket.recipientName || ticket.buyerName;

  return (
    <div className="border-2" style={{ borderColor: CYAN }}>
      {/* Welcome + the host's words. */}
      <div className="border-b-2 p-6 text-center" style={{ borderColor: 'rgba(3,255,255,0.3)' }}>
        <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: CYAN }}>
          Welcome to the Portal
        </p>
        {ticket.signalText && (
          <p className="font-serif text-xl italic leading-snug text-white">“{ticket.signalText}”</p>
        )}
        {ticket.hostDisplayName && (
          <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-gray-400">
            Hosted by {ticket.hostDisplayName}
          </p>
        )}
      </div>

      {/* The art — friends meeting up. */}
      <div className="border-b-2 px-6 py-5" style={{ borderColor: 'rgba(3,255,255,0.3)' }}>
        <SignalPassArt className="mx-auto h-auto w-full max-w-[320px] text-white" />
      </div>

      {/* Your cool pass. */}
      <div className="flex items-center justify-between border-b-2 p-5" style={{ borderColor: 'rgba(3,255,255,0.3)' }}>
        <div className="min-w-0">
          <p className="mb-1 font-mono text-[10px] uppercase tracking-widest" style={{ color: CYAN }}>Your cool pass</p>
          <p className="truncate text-base font-bold text-white">{guest}</p>
        </div>
        <div className="ml-4 text-right">
          <p className="mb-1 font-mono text-[10px] uppercase tracking-widest text-gray-500">Status</p>
          <p className="text-sm font-bold" style={{ color: checkedIn ? '#03FF00' : CYAN }}>
            {checkedIn ? 'IN THE ROOM' : "YOU'RE IN"}
          </p>
        </div>
      </div>

      {(when || ticket.eventLocationCity || ticket.venueName) && (
        <div className="border-b-2 p-5 text-center" style={{ borderColor: 'rgba(3,255,255,0.3)' }}>
          {when && <p className="text-sm text-gray-300">{when}</p>}
          {ticket.venueName && <p className="mt-1 text-sm font-bold text-white">{ticket.venueName}</p>}
          {ticket.eventLocationCity && <p className="mt-1 text-sm text-gray-400">{ticket.eventLocationCity}</p>}
        </div>
      )}

      {/* The scannable bit — same QR a real ticket carries. */}
      <div className="flex flex-col items-center p-6">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Show this at the door</p>
        <div className="bg-black p-1" style={{ boxShadow: `0 0 0 2px ${CYAN}` }}>
          <img src={qrUrl} alt="Portal pass QR code" className="h-[200px] w-[200px]" />
        </div>
        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-widest text-gray-600">
          A pass, not a ticket · just a fun way to say you&apos;re in
        </p>
      </div>
    </div>
  );
}
